import React, { useEffect, useRef, useState } from "react";
import { Camera, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const compress = (file) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale);
    c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((b) => resolve(b || file), "image/jpeg", 0.8);
    URL.revokeObjectURL(img.src);
  };
  img.onerror = () => resolve(file);
  img.src = URL.createObjectURL(file);
});

export const PhotoThumb = ({ url, onDelete, testid }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let u;
    api.get(url, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setSrc(u); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [url]);
  return (
    <div className="relative h-24 w-24 overflow-hidden rounded-xl border border-slate-200 bg-slate-100" data-testid={testid}>
      {src ? <a href={src} target="_blank" rel="noreferrer"><img src={src} alt="Notebook" className="h-full w-full object-cover" /></a> : <Loader2 className="m-auto mt-9 h-5 w-5 animate-spin text-slate-400" />}
      {onDelete && <button type="button" onClick={onDelete} className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-red-600" aria-label="Remove photo" data-testid={`${testid}-del`}><Trash2 className="h-3.5 w-3.5" /></button>}
    </div>
  );
};

export const PhotoAdder = ({ date, photos, onChanged, big }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const blob = await compress(f);
      const fd = new FormData();
      fd.append("d", date);
      fd.append("photo", blob, "notebook.jpg");
      await api.post("/student/diary/photo", fd);
      toast.success("Photo added");
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add photo"); } finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/student/diary/photo/${id}`); onChanged(); };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="photo-adder">
      {photos.map((id) => <PhotoThumb key={id} url={`/student/diary/photo/${id}`} onDelete={() => del(id)} testid={`photo-${id}`} />)}
      {photos.length < 4 && (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="photo-add-btn"
          className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-sky-400 bg-sky-50 font-semibold text-sky-800 hover:bg-sky-100 ${big ? "h-28 w-28 text-base" : "h-24 w-24 text-xs"}`}>
          {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Camera className={big ? "h-9 w-9" : "h-6 w-6"} />}
          <span className="mt-1">{busy ? "Uploading" : "Add photo"}</span>
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="photo-input" />
    </div>
  );
};
